import type { LogClient, VerifiedTreeHead } from '../core/log/client';
import { TiledClient } from '../core/tiled/client';
import { headHistory } from './state/treeHeads';

export interface ResolvedRoot {
  root: Uint8Array;
  /** where the root came from — shown to the user so they know what the proof was compared with */
  source: 'current head' | 'seen earlier' | 'computed from tiles' | 'entered by you';
}

/**
 * Finds a root hash to compare a proof against for a given tree size: the current (signed) head,
 * a head this browser saw earlier, or — for tiled logs — a recomputation from the log's own tiles.
 */
export async function resolveRoot(client: LogClient, size: number, head: VerifiedTreeHead | undefined, typed?: string, signal?: AbortSignal): Promise<ResolvedRoot | undefined> {
  const t = typed?.trim();
  if (t) return { root: parseRoot(t), source: 'entered by you' };
  if (head && head.treeSize === size) return { root: head.rootHash, source: 'current head' };
  const seen = headHistory(client.log.id).find((h) => h.treeSize === size);
  if (seen) return { root: Uint8Array.fromHex(seen.rootHashHex), source: 'seen earlier' };
  if (client instanceof TiledClient && head && size <= head.treeSize && size > 0) {
    return { root: await client.computeRoot(size, signal), source: 'computed from tiles' };
  }
  return undefined;
}

/** Accepts hex (64 chars) or base64 (44 chars) root hashes. */
export function parseRoot(s: string): Uint8Array {
  const t = s.trim();
  const bytes = /^[0-9a-fA-F]{64}$/.test(t) ? Uint8Array.fromHex(t) : Uint8Array.fromBase64(t.replace(/-/g, '+').replace(/_/g, '/'));
  if (bytes.length !== 32) throw new Error('a root hash is 32 bytes (64 hex or 44 base64 characters)');
  return bytes;
}
